import React, { useState, useCallback, useRef, useEffect, useLayoutEffect } from 'react';
import { Stage, Layer, Rect, Text, Group, Line, Transformer, Circle, Shape } from 'react-konva';
import Konva from 'konva';
import {
  Box, Typography, IconButton, Drawer, Accordion, AccordionSummary, AccordionDetails,
  List, ListItem, ListItemIcon, ListItemText, Divider, AppBar, Toolbar, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button,
  CircularProgress, ListSubheader, Chip, Badge,
  Select, MenuItem, FormControl, InputLabel,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Mic as MicIcon, MusicNote as MusicNoteIcon, Speaker as SpeakerIcon,
  RadioButtonChecked as MonitorIcon, GridOn as GridIcon, Save as SaveIcon,
  Upload as LoadIcon, Album as DrumIcon, PianoOutlined as PianoIcon,
  Delete as DeleteIcon, PowerInput as PowerIcon, TableRestaurantRounded as TableIcon,
  Expand as ExpandIcon, Laptop as LaptopIcon, Memory as MemoryIcon,
  MenuBook as MenuBookIcon, Height as HeightIcon, Tune as TuneIcon,
  DeveloperBoard as DeveloperBoardIcon, ViewAgenda as ViewAgendaIcon,
  Weekend as WeekendIcon, Chair as ChairIcon, LinearScale as LinearScaleIcon,
  AllInbox as AllInboxIcon, Article as CarpetIcon, Usb as UsbIcon,
  Headphones as HeadphonesIcon, TrendingUp as TrendingUpIcon, Router as RouterIcon,
  Tablet as TabletIcon, Link as LinkIcon, Undo as UndoIcon, Redo as RedoIcon,
  Close as CloseIcon, Title as LabelIcon, CloudDone as SavedIcon, ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import axiosInstance from '../../utils/axiosConfig';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { StageElement } from './types';
import StageElementsSearch from './StageElementsSearch';
import ChannelListEditor from './ChannelListEditor';
import { ChannelEntry, generateChannelList, mergeChannelList, diffChannelList, ChannelDiff } from '../../utils/channelList';

// ─── Constants ────────────────────────────────────────────────────────────────

const GRID_SIZE = 20;
const STAGE_W = 940;
const STAGE_H = 680;
const AUDIENCE_H = 80;
const DRAWER_WIDTH = 240;
const POWER_RADIUS = 280; // px — max distance from a power source to cover an element

// ─── Random plot name ─────────────────────────────────────────────────────────

const NAME_ADJ = [
  'Electric','Acoustic','Midnight','Golden','Neon','Velvet','Thunder',
  'Silver','Crimson','Wild','Blue','Jade','Autumn','Blazing','Cosmic',
];
const NAME_NOUN = [
  'Session','Setlist','Stage Plan','Show','Gig','Set','Performance',
  'Night','Encore','Tour Stop','Showcase','Opening','Headline',
];
const randomPlotName = () =>
  `${NAME_ADJ[Math.floor(Math.random() * NAME_ADJ.length)]} ${NAME_NOUN[Math.floor(Math.random() * NAME_NOUN.length)]}`;

// ─── Types ────────────────────────────────────────────────────────────────────

interface PlacedElement extends StageElement {
  x: number; y: number; rotation: number;
  instanceId: string; width: number; height: number;
}
interface Band {
  _id: string;
  artistName: string;
}

interface StagePlotData {
  _id?: string; name: string; description?: string;
  bandId?: Band | string | null;
  elements: PlacedElement[]; createdAt?: Date; updatedAt?: Date;
}
interface Suggestion { id: string; label: string; reason: string }

// ─── Colours ─────────────────────────────────────────────────────────────────

const typeColors: Record<string, { fill: string; stroke: string; text: string }> = {
  microphone: { fill: '#fce4ec', stroke: '#c2185b', text: '#880e4f' },
  equipment:  { fill: '#fff3e0', stroke: '#e65100', text: '#bf360c' },
};

const INSTRUMENT_FAMILIES: Record<string, string> = {
  'grand-piano':'keyboard','upright-piano':'keyboard','acoustic-piano':'keyboard',
  'electric-piano':'keyboard','organ':'keyboard','rhodes':'keyboard',
  'synthesizer':'keyboard','sampler':'keyboard','midi-controller':'keyboard',
  'trumpet':'brass','flugelhorn':'brass','cornet':'brass','trombone':'brass',
  'bass-trombone':'brass','french-horn':'brass','tuba':'brass',
  'alto-sax':'reed','tenor-sax':'reed','baritone-sax':'reed','soprano-sax':'reed',
  'clarinet':'reed','bass-clarinet':'reed','flute':'reed','electronic-wind':'reed',
  'violin':'strings','viola':'strings','cello':'strings','double-bass':'strings',
  'acoustic-guitar':'strings','electric-guitar':'strings',
  'acoustic-bass':'strings','electric-bass':'strings',
  'drum-kit':'percussion','vibraphone':'percussion','marimba':'percussion',
  'xylophone':'percussion','congas':'percussion','bongos':'percussion',
  'timbales':'percussion','percussion':'percussion',
  'vocals':'vocal',
};

const FAMILY_COLORS: Record<string, { fill: string; stroke: string; text: string }> = {
  keyboard:   { fill: '#ede7f6', stroke: '#512da8', text: '#311b92' },
  brass:      { fill: '#fff8e1', stroke: '#f9a825', text: '#e65100' },
  reed:       { fill: '#e0f2f1', stroke: '#00796b', text: '#004d40' },
  strings:    { fill: '#fbe9e7', stroke: '#c62828', text: '#b71c1c' },
  percussion: { fill: '#efebe9', stroke: '#5d4037', text: '#3e2723' },
  vocal:      { fill: '#f3e5f5', stroke: '#7b1fa2', text: '#4a148c' },
};

const getElementColors = (id: string, type: string) => {
  if (type !== 'instrument') return typeColors[type] ?? typeColors.equipment;
  const family = INSTRUMENT_FAMILIES[id];
  return (family ? FAMILY_COLORS[family] : null) ?? { fill: '#e3f2fd', stroke: '#1565c0', text: '#0d47a1' };
};

// ─── Power awareness ──────────────────────────────────────────────────────────

const NEEDS_POWER = new Set([
  'bass-amp','guitar-amp','keyboard-amp','pa-speaker-l','pa-speaker-r','subwoofer',
  'floor-monitor','side-fill-l','side-fill-r','drum-monitor','in-ear-system',
  'mixer','digital-stage-box','monitor-mixer','effects-rack','di-box-active',
  'laptop','tablet','interface','midi-controller',
  'electric-piano','organ','rhodes','synthesizer','sampler','electronic-wind',
]);

const IS_POWER_SOURCE = new Set(['power-drop','power-strip','power-distro','transformer']);

/** Returns true if the element centre is within POWER_RADIUS of any power source */
const isNearPower = (
  el: PlacedElement,
  sources: Array<{ x: number; y: number }>,
): boolean =>
  sources.some(s =>
    Math.hypot((el.x + el.width / 2) - s.x, (el.y + el.height / 2) - s.y) < POWER_RADIUS,
  );

// ─── Z-ordering ───────────────────────────────────────────────────────────────

const getZOrder = (id: string): number => {
  if (id === 'annotation') return 10;
  if (['drum-mat','cable-ramp'].includes(id)) return 0;
  if (['drum-riser','table-small','table-large'].includes(id)) return 1;
  if (['mic-stand-straight','mic-stand-boom','mic-stand-short','guitar-stand','music-stand'].includes(id)) return 8;
  if (['vocal-mic','instrument-mic','di-box'].includes(id)) return 9;
  return 5;
};

// ─── Smart positioning ────────────────────────────────────────────────────────

const SMART_POSITIONS: Record<string, (w: number, h: number) => { x: number; y: number }> = {
  'pa-speaker-l':  (w, h) => ({ x: 40,                  y: STAGE_H - AUDIENCE_H - h - 40 }),
  'pa-speaker-r':  (w, h) => ({ x: STAGE_W - w - 40,    y: STAGE_H - AUDIENCE_H - h - 40 }),
  'side-fill-l':   (w, h) => ({ x: 20,                  y: (STAGE_H - AUDIENCE_H - h) / 2 }),
  'side-fill-r':   (w, h) => ({ x: STAGE_W - w - 20,    y: (STAGE_H - AUDIENCE_H - h) / 2 }),
  'drum-kit':      (w, h) => ({ x: (STAGE_W - w) / 2,   y: 40 }),
  'grand-piano':   (_w, _h) => ({ x: 60,                 y: 40 }),
  'upright-piano': (_w, _h) => ({ x: 60,                 y: 40 }),
  'bass-amp':      (w, h) => ({ x: 80,                  y: (STAGE_H - AUDIENCE_H - h) / 2 }),
  'guitar-amp':    (w, h) => ({ x: STAGE_W - w - 80,    y: (STAGE_H - AUDIENCE_H - h) / 2 }),
  'mixer':         (w, h) => ({ x: STAGE_W - w - 40,    y: 40 }),
  'monitor-mixer': (w, h) => ({ x: STAGE_W - w - 20,    y: (STAGE_H - AUDIENCE_H - h) / 2 }),
  'floor-monitor': (w, h) => ({ x: (STAGE_W - w) / 2,  y: STAGE_H - AUDIENCE_H - h - 20 }),
};

// ─── Suggestions ─────────────────────────────────────────────────────────────

const ELEMENT_SUGGESTIONS: Record<string, Suggestion[]> = {
  'drum-kit': [
    { id: 'drum-mat',    label: 'Drum Mat',     reason: 'Stops the kit sliding' },
    { id: 'drum-riser',  label: 'Drum Riser',   reason: 'Elevate for visibility' },
    { id: 'drum-throne', label: 'Drum Throne',  reason: 'Seat for drummer' },
    { id: 'drum-monitor',label: 'Drum Monitor', reason: 'Drummer needs monitoring' },
  ],
  'grand-piano':   [{ id: 'piano-bench', label: 'Piano Bench', reason: 'Seat for pianist' }],
  'upright-piano': [{ id: 'piano-bench', label: 'Piano Bench', reason: 'Seat for pianist' }],
  'electric-piano': [
    { id: 'keyboard-stand-x', label: 'Keyboard Stand', reason: 'Support needed' },
    { id: 'piano-bench', label: 'Piano Bench', reason: 'Seat for player' },
  ],
  'acoustic-guitar': [
    { id: 'guitar-stand', label: 'Guitar Stand', reason: 'Safe resting spot' },
    { id: 'di-box', label: 'DI Box', reason: 'Direct input for acoustic' },
  ],
  'electric-guitar': [
    { id: 'guitar-amp',   label: 'Guitar Amp',   reason: 'Amplification needed' },
    { id: 'guitar-stand', label: 'Guitar Stand', reason: 'Safe resting spot' },
  ],
  'electric-bass': [{ id: 'bass-amp', label: 'Bass Amp', reason: 'Amplification needed' }],
  'bass-amp':     [{ id: 'power-drop', label: 'Power Drop', reason: 'Amp needs mains power' }],
  'guitar-amp':   [{ id: 'power-drop', label: 'Power Drop', reason: 'Amp needs mains power' }],
  'keyboard-amp': [{ id: 'power-drop', label: 'Power Drop', reason: 'Amp needs mains power' }],
  'mixer': [
    { id: 'power-drop',        label: 'Power Drop', reason: 'Console needs mains' },
    { id: 'digital-stage-box', label: 'Stage Box',  reason: 'Multi-core signal run' },
  ],
  'vocals': [
    { id: 'vocal-mic',          label: 'Vocal Mic',    reason: 'Microphone for singer' },
    { id: 'mic-stand-straight', label: 'Mic Stand',    reason: 'Stand for mic' },
    { id: 'floor-monitor',      label: 'Floor Monitor',reason: 'Singer needs monitoring' },
  ],
  'laptop': [
    { id: 'interface',   label: 'Audio Interface', reason: 'Audio I/O for laptop' },
    { id: 'power-drop',  label: 'Power Drop',      reason: 'Laptop needs mains' },
    { id: 'table-small', label: 'Small Table',     reason: 'Surface for laptop' },
  ],
  'in-ear-system': [{ id: 'power-drop', label: 'Power Drop', reason: 'Transmitter needs power' }],
};

// ─── Default sizes ────────────────────────────────────────────────────────────

const getDefaultSize = (id: string): { width: number; height: number } => {
  const overrides: Record<string, { width: number; height: number }> = {
    'grand-piano':      { width: 130, height: 110 },
    'upright-piano':    { width: 130, height: 55  },
    'acoustic-piano':   { width: 130, height: 110 },
    'electric-piano':   { width: 120, height: 55  },
    'organ':            { width: 120, height: 55  },
    'rhodes':           { width: 120, height: 55  },
    'drum-kit':         { width: 160, height: 140 },
    'drum-riser':       { width: 140, height: 110 },
    'table-large':      { width: 140, height: 70  },
    'table-small':      { width: 100, height: 55  },
    'mixer':            { width: 110, height: 75  },
    'keyboard-stand-x': { width: 110, height: 45  },
    'keyboard-stand-z': { width: 110, height: 45  },
    'marimba':          { width: 130, height: 55  },
    'vibraphone':       { width: 120, height: 55  },
    'xylophone':        { width: 110, height: 50  },
    'annotation':       { width: 140, height: 36  },
  };
  if (overrides[id]) return overrides[id];
  const xl = ['pa-speaker-l','pa-speaker-r','subwoofer','acoustic-bass'];
  const lg = ['bass-amp','guitar-amp','keyboard-amp','acoustic-guitar','digital-stage-box','monitor-mixer'];
  const sm = ['mic-stand-straight','mic-stand-boom','mic-stand-short','interface','tablet'];
  const xs = ['di-box','di-box-active','di-box-passive','cable-ties','gaffer-tape','vocal-mic','instrument-mic','power-strip','music-stand','guitar-stand'];
  if (xl.includes(id)) return { width: 90, height: 90 };
  if (lg.includes(id)) return { width: 80, height: 80 };
  if (sm.includes(id)) return { width: 55, height: 55 };
  if (xs.includes(id)) return { width: 48, height: 48 };
  return { width: 68, height: 68 };
};

// ─── Element library ───────────────────────────────────────────────────────────

const stageElements: { [cat: string]: { [subcat: string]: StageElement[] } } = {
  microphones: {
    '': [
      { id: 'vocal-mic',      name: 'Vocal Mic',      icon: <MicIcon />, type: 'microphone' },
      { id: 'instrument-mic', name: 'Instrument Mic', icon: <MicIcon />, type: 'microphone' },
      { id: 'di-box',         name: 'DI Box',         icon: <MicIcon />, type: 'microphone' },
    ],
  },
  instruments: {
    'Rhythm Section': [
      { id: 'grand-piano',    name: 'Grand Piano',    icon: <PianoIcon />,     type: 'instrument' },
      { id: 'upright-piano',  name: 'Upright Piano',  icon: <PianoIcon />,     type: 'instrument' },
      { id: 'electric-piano', name: 'Electric Piano', icon: <PianoIcon />,     type: 'instrument' },
      { id: 'organ',          name: 'Organ',          icon: <PianoIcon />,     type: 'instrument' },
      { id: 'rhodes',         name: 'Rhodes',         icon: <PianoIcon />,     type: 'instrument' },
      { id: 'acoustic-guitar',name: 'Acoustic Guitar',icon: <MusicNoteIcon />, type: 'instrument' },
      { id: 'electric-guitar',name: 'Electric Guitar',icon: <MusicNoteIcon />, type: 'instrument' },
      { id: 'acoustic-bass',  name: 'Acoustic Bass',  icon: <MusicNoteIcon />, type: 'instrument' },
      { id: 'electric-bass',  name: 'Electric Bass',  icon: <MusicNoteIcon />, type: 'instrument' },
      { id: 'drum-kit',       name: 'Drum Kit',       icon: <DrumIcon />,      type: 'instrument' },
    ],
    'Brass Section': [
      { id: 'trumpet',       name: 'Trumpet',       icon: <MusicNoteIcon />, type: 'instrument' },
      { id: 'flugelhorn',    name: 'Flugelhorn',    icon: <MusicNoteIcon />, type: 'instrument' },
      { id: 'cornet',        name: 'Cornet',        icon: <MusicNoteIcon />, type: 'instrument' },
      { id: 'trombone',      name: 'Trombone',      icon: <MusicNoteIcon />, type: 'instrument' },
      { id: 'bass-trombone', name: 'Bass Trombone', icon: <MusicNoteIcon />, type: 'instrument' },
      { id: 'french-horn',   name: 'French Horn',   icon: <MusicNoteIcon />, type: 'instrument' },
      { id: 'tuba',          name: 'Tuba',          icon: <MusicNoteIcon />, type: 'instrument' },
      { id: 'generic-horn',  name: 'Other Horn',    icon: <MusicNoteIcon />, type: 'instrument' },
    ],
    'Reed Section': [
      { id: 'alto-sax',      name: 'Alto Saxophone',    icon: <MusicNoteIcon />, type: 'instrument' },
      { id: 'tenor-sax',     name: 'Tenor Saxophone',   icon: <MusicNoteIcon />, type: 'instrument' },
      { id: 'baritone-sax',  name: 'Baritone Saxophone',icon: <MusicNoteIcon />, type: 'instrument' },
      { id: 'soprano-sax',   name: 'Soprano Saxophone', icon: <MusicNoteIcon />, type: 'instrument' },
      { id: 'clarinet',      name: 'Clarinet',          icon: <MusicNoteIcon />, type: 'instrument' },
      { id: 'bass-clarinet', name: 'Bass Clarinet',     icon: <MusicNoteIcon />, type: 'instrument' },
      { id: 'flute',         name: 'Flute',             icon: <MusicNoteIcon />, type: 'instrument' },
      { id: 'generic-reed',  name: 'Other Reed',        icon: <MusicNoteIcon />, type: 'instrument' },
    ],
    'Strings': [
      { id: 'violin',         name: 'Violin',       icon: <MusicNoteIcon />, type: 'instrument' },
      { id: 'viola',          name: 'Viola',        icon: <MusicNoteIcon />, type: 'instrument' },
      { id: 'cello',          name: 'Cello',        icon: <MusicNoteIcon />, type: 'instrument' },
      { id: 'double-bass',    name: 'Double Bass',  icon: <MusicNoteIcon />, type: 'instrument' },
      { id: 'generic-string', name: 'Other String', icon: <MusicNoteIcon />, type: 'instrument' },
    ],
    'Percussion / Mallet': [
      { id: 'vibraphone',         name: 'Vibraphone',       icon: <MusicNoteIcon />, type: 'instrument' },
      { id: 'marimba',            name: 'Marimba',          icon: <MusicNoteIcon />, type: 'instrument' },
      { id: 'xylophone',          name: 'Xylophone',        icon: <MusicNoteIcon />, type: 'instrument' },
      { id: 'congas',             name: 'Congas',           icon: <DrumIcon />,      type: 'instrument' },
      { id: 'bongos',             name: 'Bongos',           icon: <DrumIcon />,      type: 'instrument' },
      { id: 'timbales',           name: 'Timbales',         icon: <DrumIcon />,      type: 'instrument' },
      { id: 'percussion',         name: 'Percussion',       icon: <DrumIcon />,      type: 'instrument' },
      { id: 'generic-percussion', name: 'Other Percussion', icon: <DrumIcon />,      type: 'instrument' },
    ],
    'Modern / Electronic': [
      { id: 'synthesizer',        name: 'Synthesizer',      icon: <PianoIcon />,     type: 'instrument' },
      { id: 'sampler',            name: 'Sampler',          icon: <PianoIcon />,     type: 'instrument' },
      { id: 'electronic-wind',    name: 'EWI',              icon: <MusicNoteIcon />, type: 'instrument' },
      { id: 'generic-electronic', name: 'Other Electronic', icon: <PianoIcon />,     type: 'instrument' },
    ],
    'Vocal': [
      { id: 'vocals',       name: 'Vocals',      icon: <MusicNoteIcon />, type: 'instrument' },
      { id: 'generic-vocal',name: 'Other Vocal', icon: <MusicNoteIcon />, type: 'instrument' },
    ],
    'Custom': [
      { id: 'custom', name: 'Custom / Other', icon: <MusicNoteIcon />, type: 'instrument' },
    ],
  },
  equipment: {
    'Amplification': [
      { id: 'bass-amp',     name: 'Bass Amp',       icon: <SpeakerIcon />, type: 'equipment' },
      { id: 'guitar-amp',   name: 'Guitar Amp',     icon: <SpeakerIcon />, type: 'equipment' },
      { id: 'keyboard-amp', name: 'Keyboard Amp',   icon: <SpeakerIcon />, type: 'equipment' },
      { id: 'pa-speaker-l', name: 'PA Speaker (L)', icon: <SpeakerIcon />, type: 'equipment' },
      { id: 'pa-speaker-r', name: 'PA Speaker (R)', icon: <SpeakerIcon />, type: 'equipment' },
      { id: 'subwoofer',    name: 'Subwoofer',      icon: <SpeakerIcon />, type: 'equipment' },
    ],
    'Monitoring': [
      { id: 'floor-monitor', name: 'Floor Monitor',  icon: <MonitorIcon />,    type: 'equipment' },
      { id: 'side-fill-l',   name: 'Side Fill (L)',  icon: <MonitorIcon />,    type: 'equipment' },
      { id: 'side-fill-r',   name: 'Side Fill (R)',  icon: <MonitorIcon />,    type: 'equipment' },
      { id: 'drum-monitor',  name: 'Drum Monitor',   icon: <MonitorIcon />,    type: 'equipment' },
      { id: 'in-ear-system', name: 'In-Ear System',  icon: <HeadphonesIcon />, type: 'equipment' },
    ],
    'Stands': [
      { id: 'music-stand',        name: 'Music Stand',        icon: <MenuBookIcon />,   type: 'equipment' },
      { id: 'keyboard-stand-x',   name: 'Keyboard Stand (X)', icon: <ExpandIcon />,     type: 'equipment' },
      { id: 'keyboard-stand-z',   name: 'Keyboard Stand (Z)', icon: <ExpandIcon />,     type: 'equipment' },
      { id: 'guitar-stand',       name: 'Guitar Stand',       icon: <ExpandIcon />,     type: 'equipment' },
      { id: 'mic-stand-straight', name: 'Mic Stand',          icon: <HeightIcon />,     type: 'equipment' },
      { id: 'mic-stand-boom',     name: 'Mic Stand (Boom)',   icon: <TrendingUpIcon />, type: 'equipment' },
      { id: 'mic-stand-short',    name: 'Mic Stand (Short)',  icon: <HeightIcon />,     type: 'equipment' },
    ],
    'Power': [
      { id: 'power-drop',  name: 'Power Drop',         icon: <PowerIcon />, type: 'equipment' },
      { id: 'power-strip', name: 'Power Strip',        icon: <PowerIcon />, type: 'equipment' },
      { id: 'power-distro',name: 'Power Distribution', icon: <PowerIcon />, type: 'equipment' },
      { id: 'transformer', name: 'Transformer',        icon: <PowerIcon />, type: 'equipment' },
    ],
    'Mixing': [
      { id: 'mixer',             name: 'Mixing Console',   icon: <TuneIcon />,   type: 'equipment' },
      { id: 'digital-stage-box', name: 'Digital Stage Box',icon: <RouterIcon />, type: 'equipment' },
      { id: 'monitor-mixer',     name: 'Monitor Mixer',    icon: <TuneIcon />,   type: 'equipment' },
    ],
    'Effects': [
      { id: 'effects-rack',   name: 'Effects Rack',    icon: <DeveloperBoardIcon />, type: 'equipment' },
      { id: 'di-box-active',  name: 'DI Box (Active)', icon: <MemoryIcon />,         type: 'equipment' },
      { id: 'di-box-passive', name: 'DI Box (Passive)',icon: <MemoryIcon />,         type: 'equipment' },
    ],
    'Furniture': [
      { id: 'table-small', name: 'Small Table', icon: <TableIcon />,      type: 'equipment' },
      { id: 'table-large', name: 'Large Table', icon: <TableIcon />,      type: 'equipment' },
      { id: 'drum-riser',  name: 'Drum Riser',  icon: <ViewAgendaIcon />, type: 'equipment' },
      { id: 'piano-bench', name: 'Piano Bench', icon: <WeekendIcon />,    type: 'equipment' },
      { id: 'drum-throne', name: 'Drum Throne', icon: <WeekendIcon />,    type: 'equipment' },
      { id: 'chair',       name: 'Chair',       icon: <ChairIcon />,      type: 'equipment' },
      { id: 'bar-stool',   name: 'Bar Stool',   icon: <ChairIcon />,      type: 'equipment' },
    ],
    'Cables': [
      { id: 'xlr-cable',        name: 'XLR Cable',        icon: <LinearScaleIcon />, type: 'equipment' },
      { id: 'instrument-cable', name: 'Instrument Cable', icon: <LinearScaleIcon />, type: 'equipment' },
      { id: 'speaker-cable',    name: 'Speaker Cable',    icon: <LinearScaleIcon />, type: 'equipment' },
      { id: 'power-cable',      name: 'Power Cable',      icon: <LinearScaleIcon />, type: 'equipment' },
      { id: 'multi-core',       name: 'Multi-core Cable', icon: <LinearScaleIcon />, type: 'equipment' },
    ],
    'Technology': [
      { id: 'laptop',          name: 'Laptop',          icon: <LaptopIcon />, type: 'equipment' },
      { id: 'tablet',          name: 'Tablet',          icon: <TabletIcon />, type: 'equipment' },
      { id: 'interface',       name: 'Audio Interface', icon: <UsbIcon />,    type: 'equipment' },
      { id: 'midi-controller', name: 'MIDI Controller', icon: <PianoIcon />,  type: 'equipment' },
    ],
    'Accessories': [
      { id: 'drum-mat',    name: 'Drum Mat',    icon: <CarpetIcon />,      type: 'equipment' },
      { id: 'cable-ramp',  name: 'Cable Ramp',  icon: <LinearScaleIcon />, type: 'equipment' },
      { id: 'cable-ties',  name: 'Cable Ties',  icon: <LinkIcon />,        type: 'equipment' },
      { id: 'gaffer-tape', name: 'Gaffer Tape', icon: <AllInboxIcon />,    type: 'equipment' },
    ],
  },
};

const findElementDef = (id: string): StageElement | undefined => {
  for (const cat of Object.values(stageElements)) {
    for (const subcat of Object.values(cat)) {
      const match = subcat.find(e => e.id === id);
      if (match) return match;
    }
  }
  return undefined;
};

// ─── Custom shapes ────────────────────────────────────────────────────────────

interface ShapeProps {
  width: number; height: number; name: string;
  isSelected: boolean;
  colors: { fill: string; stroke: string; text: string };
}

const GrandPianoShape: React.FC<ShapeProps> = ({ width: w, height: h, name, isSelected, colors }) => {
  const stroke = isSelected ? '#1976d2' : colors.stroke;
  const sw = isSelected ? 2.5 : 1.5;
  // Keyboard occupies bottom 16% — narrower than full width on treble side
  const keyY  = h * 0.84;
  const keyW  = w * 0.86; // keyboard doesn't reach full width (cheekblock inset)
  return (
    <>
      {/* Transparent hit-area rect so Konva transformer bounds are correct */}
      <Rect width={w} height={h} fill="rgba(0,0,0,0.001)" strokeWidth={0} />

      {/* ── Grand piano body silhouette (top-down) ──────────────────────────
          Orientation: keyboard at BOTTOM, spine (bass) on LEFT, tail at TOP.
          Real-piano anatomy:
            • LEFT  = spine: straight edge from keyboard to tail
            • BOTTOM = keyboard: straight, slightly inset on treble end
            • BOTTOM-RIGHT corner = cheekblock: small angled cut
            • RIGHT  = curved treble rim: stays wide, then sweeps inward
            • TOP    = tail: rounded point where rim meets spine          */}
      <Shape
        sceneFunc={(ctx, shape) => {
          ctx.beginPath();

          // 1. Bass end of keyboard — spine/keyboard junction (bottom-left)
          ctx.moveTo(0, h);

          // 2. Keyboard bottom edge (straight, slightly short of full width)
          ctx.lineTo(keyW, h);

          // 3. Cheekblock: small diagonal cut at treble-keyboard corner
          ctx.lineTo(w, h * 0.86);

          // 4. Treble curved rim
          //    - Holds near full width for the lower half (the wide bass body)
          //    - Then sweeps LEFT, narrowing toward the tail
          ctx.bezierCurveTo(
            w,        h * 0.52,   // hold right edge going up
            w * 0.78, h * 0.18,  // swing hard left mid-way up
            w * 0.1,  h * 0.02,  // arrive near top-left (tail)
          );

          // 5. Tail: small rounded corner
          ctx.bezierCurveTo(
            w * 0.03, 0,         // round the tail corner
            0,        h * 0.04,
            0,        h * 0.14,  // onto spine
          );

          // 6. Spine: straight line down the left (bass) side back to keyboard
          ctx.lineTo(0, h);

          ctx.closePath();
          ctx.fillStrokeShape(shape);
        }}
        fill={colors.fill} stroke={stroke} strokeWidth={sw}
        shadowEnabled={isSelected} shadowBlur={10} shadowOpacity={0.35} shadowColor="#1976d2"
        listening={false}
      />

      {/* Keyboard strip */}
      <Shape
        sceneFunc={(ctx, shape) => {
          ctx.beginPath();
          ctx.moveTo(0, h);
          ctx.lineTo(keyW, h);
          ctx.lineTo(keyW, keyY);
          ctx.lineTo(0, keyY);
          ctx.closePath();
          ctx.fillStrokeShape(shape);
        }}
        fill="rgba(255,255,255,0.5)" stroke={colors.stroke} strokeWidth={0.8}
        listening={false}
      />
      {/* Key dividers */}
      {Array.from({ length: 7 }).map((_, i) => {
        const x = (i + 1) * keyW / 8;
        return <Line key={i} points={[x, keyY, x, h]} stroke={colors.stroke} strokeWidth={0.7} opacity={0.45} listening={false} />;
      })}

      {/* Label — centred in the body */}
      <Text
        text={name}
        x={w * 0.04} y={h * 0.36} width={w * 0.7} align="center"
        fontSize={Math.max(8, Math.min(13, Math.floor(w / 10)))}
        fontFamily="'Inter','Roboto',sans-serif" fontStyle="bold"
        fill={colors.text} listening={false}
      />
    </>
  );
};

const KeyboardShape: React.FC<ShapeProps> = ({ width: w, height: h, name, isSelected, colors }) => {
  const stroke = isSelected ? '#1976d2' : colors.stroke;
  const sw = isSelected ? 2.5 : 1.5;
  const keyH = Math.min(h * 0.38, 18);
  return (
    <>
      <Rect width={w} height={h} fill={colors.fill} stroke={stroke} strokeWidth={sw} cornerRadius={3}
        shadowEnabled={isSelected} shadowBlur={10} shadowOpacity={0.35} shadowColor="#1976d2" />
      <Rect x={3} y={h - keyH - 3} width={w - 6} height={keyH}
        fill="rgba(255,255,255,0.55)" stroke={colors.stroke} strokeWidth={0.8} cornerRadius={2} listening={false} />
      {Array.from({ length: 7 }).map((_, i) => {
        const x = 3 + (i + 1) * (w - 6) / 8;
        return <Line key={i} points={[x, h - keyH - 3, x, h - 3]} stroke={colors.stroke} strokeWidth={0.7} opacity={0.4} listening={false} />;
      })}
      <Text
        text={name} x={0} y={h * 0.18} width={w} align="center"
        fontSize={Math.max(8, Math.min(13, Math.floor(w / 10)))}
        fontFamily="'Inter','Roboto',sans-serif" fontStyle="bold" fill={colors.text} listening={false}
      />
    </>
  );
};

const DrumKitShape: React.FC<ShapeProps> = ({ width: w, height: h, isSelected, colors }) => {
  const r = (fr: number) => fr * Math.min(w, h);
  const drums = [
    { key: 'kick',  cx: 0.48, cy: 0.70, fr: 0.21, fill: colors.fill },
    { key: 'snare', cx: 0.27, cy: 0.53, fr: 0.12, fill: '#fffde7' },
    { key: 'ft',    cx: 0.76, cy: 0.57, fr: 0.14, fill: colors.fill },
    { key: 't1',    cx: 0.34, cy: 0.24, fr: 0.11, fill: colors.fill },
    { key: 't2',    cx: 0.58, cy: 0.24, fr: 0.11, fill: colors.fill },
    { key: 'hh',    cx: 0.10, cy: 0.44, fr: 0.08, fill: '#e0e0e0' },
    { key: 'ride',  cx: 0.88, cy: 0.35, fr: 0.07, fill: '#e0e0e0' },
  ];
  const labels = ['Kick','Snare','FT','T1','T2','HH','Ride'];
  return (
    <>
      <Rect width={w} height={h} fill="transparent"
        stroke={isSelected ? '#1976d2' : 'transparent'}
        strokeWidth={isSelected ? 2 : 0} cornerRadius={4}
        shadowEnabled={isSelected} shadowBlur={10} shadowOpacity={0.35} shadowColor="#1976d2"
      />
      {drums.map((d, i) => (
        <React.Fragment key={d.key}>
          <Circle x={d.cx * w} y={d.cy * h} radius={r(d.fr)} fill={d.fill} stroke={colors.stroke} strokeWidth={1.5} />
          <Text text={labels[i]} x={d.cx * w - 16} y={d.cy * h - 5} width={32} align="center"
            fontSize={7} fontStyle="bold" fontFamily="'Inter','Roboto',sans-serif" fill={colors.text} listening={false} />
        </React.Fragment>
      ))}
    </>
  );
};

// ─── Sidebar ──────────────────────────────────────────────────────────────────

const getCategoryColor = (cat: string) =>
  cat === 'microphones' ? '#e91e63' : cat === 'instruments' ? '#2196f3' : '#ff9800';

const DraggableElement: React.FC<{
  element: StageElement;
  onAdd: (element: StageElement, smart: boolean) => void;
}> = ({ element, onAdd }) => {
  const hasSmart = Boolean(SMART_POSITIONS[element.id]);

  const handleDragStart = (e: React.DragEvent) => {
    e.dataTransfer.setData('application/json', JSON.stringify({ id: element.id, name: element.name, type: element.type }));
    e.dataTransfer.effectAllowed = 'copy';
  };

  return (
    <Tooltip title={hasSmart ? 'Double-click → smart position' : 'Double-click → add to centre'} placement="right" enterDelay={700}>
      <ListItem
        draggable
        onDragStart={handleDragStart}
        onDoubleClick={e => { e.stopPropagation(); onAdd(element, hasSmart); }}
        sx={{
          cursor: 'grab', '&:hover': { backgroundColor: 'action.hover' }, '&:active': { cursor: 'grabbing' },
          padding: '4px 8px', height: 40, borderRadius: 1, userSelect: 'none',
          borderLeft: hasSmart ? '2px solid #bbdefb' : '2px solid transparent',
        }}
      >
        <ListItemIcon sx={{ minWidth: 36 }}>
          {React.cloneElement(element.icon, {
            fontSize: 'small',
            color: element.type === 'microphone' ? 'error' : element.type === 'instrument' ? 'primary' : 'warning',
          })}
        </ListItemIcon>
        <ListItemText primary={element.name} primaryTypographyProps={{ variant: 'body2', sx: { fontWeight: 500 } }} />
        {NEEDS_POWER.has(element.id) && <Typography sx={{ fontSize: 11, opacity: 0.6 }}>⚡</Typography>}
      </ListItem>
    </Tooltip>
  );
};

const StageElementsDrawer: React.FC<{ onElementAdd: (el: StageElement, smart: boolean) => void }> = ({ onElementAdd }) => {
  const [expanded, setExpanded] = useState<string | false>('instruments');

  return (
    <Drawer variant="permanent" sx={{ width: DRAWER_WIDTH, flexShrink: 0, '& .MuiDrawer-paper': { width: DRAWER_WIDTH, boxSizing: 'border-box', backgroundColor: 'background.default' } }}>
      <Toolbar />
      <Box sx={{ overflow: 'auto' }}>
        <StageElementsSearch stageElements={stageElements} onElementSelect={el => onElementAdd(el, false)} />
        <Divider sx={{ my: 1 }} />
        {Object.entries(stageElements).map(([category, subcategories]) => {
          const totalCount = Object.values(subcategories).reduce((sum, els) => sum + els.length, 0);
          return (
            <Accordion key={category} expanded={expanded === category}
              onChange={(_, isExpanded) => setExpanded(isExpanded ? category : false)}
              sx={{ backgroundColor: 'transparent', boxShadow: 'none', '&.Mui-expanded': { margin: 0 }, '&:before': { display: 'none' } }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}
                sx={{ borderLeft: 3, borderColor: getCategoryColor(category), '&.Mui-expanded': { minHeight: 48, backgroundColor: 'rgba(0,0,0,0.04)' }, '& .MuiAccordionSummary-content.Mui-expanded': { margin: '12px 0' } }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 500, color: getCategoryColor(category), textTransform: 'capitalize' }}>
                    {category}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.disabled', fontWeight: 400 }}>
                    ({totalCount})
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ padding: 1 }}>
                {Object.entries(subcategories).map(([subcat, elements]) => (
                  <React.Fragment key={subcat}>
                    {subcat && <ListSubheader sx={{ backgroundColor: 'inherit', color: 'text.secondary', textTransform: 'capitalize', lineHeight: '24px', px: 2 }}>{subcat}</ListSubheader>}
                    <List disablePadding>
                      {elements.map(el => <DraggableElement key={el.id} element={el} onAdd={onElementAdd} />)}
                    </List>
                  </React.Fragment>
                ))}
              </AccordionDetails>
            </Accordion>
          );
        })}
      </Box>
    </Drawer>
  );
};

// ─── Konva canvas ─────────────────────────────────────────────────────────────

interface SelRect { x: number; y: number; w: number; h: number }

const KonvaCanvas: React.FC<{
  showGrid: boolean; gridSize: number; scale: number;
  isLabelMode: boolean;
  placedElements: PlacedElement[];
  selectedIds: string[];
  powerSources: Array<{ x: number; y: number }>;
  onSelect: (id: string | null, shift: boolean) => void;
  onSelectMultiple: (ids: string[]) => void;
  onChange: (instanceId: string, updates: Partial<PlacedElement>) => void;
  onGroupMove: (updates: Record<string, { x: number; y: number }>) => void;
  onDrop: (data: { id: string; name: string; type: string }, x: number, y: number) => void;
  onLabelPlace: (pos: { x: number; y: number }) => void;
  onEditAnnotation: (instanceId: string, currentText: string) => void;
  stageRef: React.RefObject<Konva.Stage>;
}> = ({
  showGrid, gridSize, scale, isLabelMode,
  placedElements, selectedIds, powerSources,
  onSelect, onSelectMultiple, onChange, onGroupMove,
  onDrop, onLabelPlace, onEditAnnotation, stageRef,
}) => {
  const trRef = useRef<Konva.Transformer>(null);
  const nodeRefs = useRef<Map<string, Konva.Group>>(new Map());
  const dragStartRef = useRef<Map<string, { x: number; y: number }>>(new Map());
  const isDraggingGroup = useRef(false);
  const selStartRef = useRef<{ x: number; y: number } | null>(null);
  const isDrawingSel = useRef(false);
  const [selRect, setSelRect] = useState<SelRect | null>(null);

  useEffect(() => {
    if (!trRef.current) return;
    if (selectedIds.length === 1) {
      const node = nodeRefs.current.get(selectedIds[0]);
      trRef.current.nodes(node ? [node] : []);
    } else {
      trRef.current.nodes([]);
    }
    trRef.current.getLayer()?.batchDraw();
  }, [selectedIds, placedElements]);

  const handleCanvasDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const raw = e.dataTransfer.getData('application/json');
    if (!raw) return;
    const data = JSON.parse(raw);
    stageRef.current?.setPointersPositions(e.nativeEvent);
    // getRelativePointerPosition accounts for stage scale → world coordinates
    const pos = stageRef.current?.getRelativePointerPosition();
    if (!pos) return;
    const { width, height } = getDefaultSize(data.id);
    const x = Math.round((pos.x - width / 2) / gridSize) * gridSize;
    const y = Math.round((pos.y - height / 2) / gridSize) * gridSize;
    onDrop(data, Math.max(0, x), Math.max(0, Math.min(y, STAGE_H - AUDIENCE_H - height)));
  };

  const usableH = STAGE_H - AUDIENCE_H;
  const gridLines: React.ReactNode[] = [];
  if (showGrid) {
    for (let x = 0; x <= STAGE_W; x += gridSize) gridLines.push(<Line key={`v${x}`} points={[x, 0, x, usableH]} stroke="#e4e4e4" strokeWidth={0.5} listening={false} />);
    for (let y = 0; y <= usableH; y += gridSize) gridLines.push(<Line key={`h${y}`} points={[0, y, STAGE_W, y]} stroke="#e4e4e4" strokeWidth={0.5} listening={false} />);
  }

  const sorted = [...placedElements].sort((a, b) => getZOrder(a.id) - getZOrder(b.id));

  return (
    <Box
      onDrop={handleCanvasDrop}
      onDragOver={e => e.preventDefault()}
      sx={{
        border: '1px solid rgba(0,0,0,0.15)', borderRadius: 1, overflow: 'hidden',
        boxShadow: 2, display: 'inline-block',
        cursor: isLabelMode ? 'text' : 'default',
        outline: isLabelMode ? '2px solid #f9a825' : 'none',
      }}
    >
      <Stage
        ref={stageRef}
        width={STAGE_W * scale}
        height={STAGE_H * scale}
        scale={{ x: scale, y: scale }}
        onClick={e => {
          if (e.target !== e.target.getStage()) return;
          if (isLabelMode) {
            const pos = stageRef.current!.getRelativePointerPosition()!;
            onLabelPlace(pos);
          } else {
            onSelect(null, false);
          }
        }}
        onMouseDown={e => {
          if (e.target !== e.target.getStage()) return;
          if (isLabelMode) return;
          isDrawingSel.current = true;
          const pos = stageRef.current!.getRelativePointerPosition()!;
          selStartRef.current = pos;
          setSelRect({ x: pos.x, y: pos.y, w: 0, h: 0 });
        }}
        onMouseMove={() => {
          if (!isDrawingSel.current || !selStartRef.current) return;
          const pos = stageRef.current!.getRelativePointerPosition()!;
          const s = selStartRef.current;
          setSelRect({ x: Math.min(s.x, pos.x), y: Math.min(s.y, pos.y), w: Math.abs(pos.x - s.x), h: Math.abs(pos.y - s.y) });
        }}
        onMouseUp={() => {
          if (!isDrawingSel.current) return;
          isDrawingSel.current = false;
          if (selRect && selRect.w > 8 && selRect.h > 8) {
            const ids = placedElements
              .filter(el => el.x < selRect.x + selRect.w && el.x + el.width > selRect.x && el.y < selRect.y + selRect.h && el.y + el.height > selRect.y)
              .map(el => el.instanceId);
            if (ids.length) onSelectMultiple(ids);
          }
          setSelRect(null);
          selStartRef.current = null;
        }}
      >
        {/* Background + grid */}
        <Layer listening={false}>
          <Rect x={0} y={0} width={STAGE_W} height={usableH} fill="#ffffff" />
          {gridLines}
          <Line points={[0, usableH, STAGE_W, usableH]} stroke="#555" strokeWidth={3} />
        </Layer>

        {/* Elements */}
        <Layer>
          {sorted.map(element => {
            const colors = getElementColors(element.id, element.type);
            const isSelected = selectedIds.includes(element.instanceId);
            const needsPower = NEEDS_POWER.has(element.id);
            const showPowerBadge = needsPower && !isNearPower(element, powerSources);
            const fontSize = Math.max(9, Math.min(13, Math.floor(Math.min(element.width, element.height) / 6)));
            const isGroupDrag = isSelected && selectedIds.length > 1;

            const sp = { width: element.width, height: element.height, name: element.name, isSelected, colors };

            const renderContent = () => {
              if (element.id === 'annotation') {
                return (
                  <>
                    <Rect
                      width={element.width} height={element.height}
                      fill="rgba(255, 248, 196, 0.85)"
                      stroke={isSelected ? '#1976d2' : '#f9a825'}
                      strokeWidth={isSelected ? 2 : 1}
                      dash={isSelected ? undefined : [5, 3]}
                      cornerRadius={4}
                      shadowEnabled={isSelected} shadowBlur={8} shadowOpacity={0.3} shadowColor="#1976d2"
                    />
                    <Text
                      text={element.name}
                      width={element.width} height={element.height}
                      align="center" verticalAlign="middle"
                      fontSize={Math.max(10, Math.min(14, Math.floor(element.width / 10)))}
                      fontFamily="'Inter','Roboto',sans-serif" fontStyle="bold"
                      fill="#5d4037" padding={6} wrap="word" listening={false}
                    />
                  </>
                );
              }
              if (element.id === 'grand-piano' || element.id === 'acoustic-piano') return <GrandPianoShape {...sp} />;
              if (['upright-piano','electric-piano','organ','rhodes'].includes(element.id)) return <KeyboardShape {...sp} />;
              if (element.id === 'drum-kit') return <DrumKitShape {...sp} />;
              return (
                <>
                  <Rect
                    width={element.width} height={element.height}
                    fill={colors.fill}
                    stroke={isSelected ? '#1976d2' : colors.stroke}
                    strokeWidth={isSelected ? 2.5 : 1.5}
                    cornerRadius={5}
                    shadowEnabled={isSelected} shadowBlur={10} shadowOpacity={0.35} shadowColor="#1976d2"
                  />
                  <Text
                    text={element.name} width={element.width} height={element.height}
                    align="center" verticalAlign="middle" fontSize={fontSize}
                    fontFamily="'Inter','Roboto',sans-serif" fontStyle="bold"
                    fill={colors.text} padding={4} wrap="word" listening={false}
                  />
                </>
              );
            };

            return (
              <Group
                key={element.instanceId}
                ref={node => { if (node) nodeRefs.current.set(element.instanceId, node); else nodeRefs.current.delete(element.instanceId); }}
                x={element.x} y={element.y}
                rotation={element.rotation}
                draggable
                dragBoundFunc={isGroupDrag ? undefined : pos => ({
                  x: Math.round(pos.x / gridSize) * gridSize,
                  y: Math.max(0, Math.min(Math.round(pos.y / gridSize) * gridSize, usableH - element.height)),
                })}
                onClick={e => { e.cancelBubble = true; onSelect(element.instanceId, e.evt.shiftKey); }}
                onTap={e => { e.cancelBubble = true; onSelect(element.instanceId, false); }}
                onDblClick={e => {
                  if (element.id === 'annotation') {
                    e.cancelBubble = true;
                    onEditAnnotation(element.instanceId, element.name);
                  }
                }}
                onDragStart={() => {
                  if (!isGroupDrag) return;
                  isDraggingGroup.current = true;
                  dragStartRef.current.clear();
                  placedElements.forEach(el => {
                    if (selectedIds.includes(el.instanceId))
                      dragStartRef.current.set(el.instanceId, { x: el.x, y: el.y });
                  });
                }}
                onDragMove={e => {
                  if (!isDraggingGroup.current || !isSelected || selectedIds.length <= 1) return;
                  const start = dragStartRef.current.get(element.instanceId);
                  if (!start) return;
                  const dx = e.target.x() - start.x;
                  const dy = e.target.y() - start.y;
                  selectedIds.forEach(id => {
                    if (id === element.instanceId) return;
                    const node = nodeRefs.current.get(id);
                    const otherStart = dragStartRef.current.get(id);
                    if (!node || !otherStart) return;
                    node.x(otherStart.x + dx);
                    node.y(otherStart.y + dy);
                  });
                }}
                onDragEnd={e => {
                  const newX = e.target.x();
                  const newY = e.target.y();
                  if (isDraggingGroup.current && isGroupDrag) {
                    isDraggingGroup.current = false;
                    const start = dragStartRef.current.get(element.instanceId);
                    const dx = start ? newX - start.x : 0;
                    const dy = start ? newY - start.y : 0;
                    const updates: Record<string, { x: number; y: number }> = {};
                    selectedIds.forEach(id => {
                      const otherStart = dragStartRef.current.get(id);
                      if (!otherStart) return;
                      const el = placedElements.find(p => p.instanceId === id);
                      updates[id] = {
                        x: Math.max(0, Math.round((otherStart.x + dx) / gridSize) * gridSize),
                        y: Math.max(0, Math.min(Math.round((otherStart.y + dy) / gridSize) * gridSize, usableH - (el?.height ?? 60))),
                      };
                    });
                    onGroupMove(updates);
                  } else {
                    onChange(element.instanceId, { x: newX, y: newY });
                  }
                }}
                onTransformEnd={() => {
                  const node = nodeRefs.current.get(element.instanceId);
                  if (!node) return;
                  const scaleX = node.scaleX(); const scaleY = node.scaleY();
                  node.scaleX(1); node.scaleY(1);
                  onChange(element.instanceId, {
                    x: node.x(), y: node.y(), rotation: node.rotation(),
                    width: Math.max(40, Math.round(element.width * scaleX / gridSize) * gridSize),
                    height: Math.max(40, Math.round(element.height * scaleY / gridSize) * gridSize),
                  });
                }}
              >
                {renderContent()}
                {showPowerBadge && (
                  <>
                    <Circle x={element.width - 8} y={8} radius={9} fill="#ffc107" strokeWidth={0} listening={false} />
                    <Text text="⚡" x={element.width - 17} y={1} width={18} align="center" fontSize={10} listening={false} />
                  </>
                )}
              </Group>
            );
          })}

          {selRect && selRect.w > 2 && selRect.h > 2 && (
            <Rect
              x={selRect.x} y={selRect.y} width={selRect.w} height={selRect.h}
              fill="rgba(25,118,210,0.07)" stroke="#1976d2" strokeWidth={1}
              dash={[4, 3]} listening={false}
            />
          )}

          <Transformer
            ref={trRef}
            rotateEnabled keepRatio={false}
            rotationSnaps={[0, 45, 90, 135, 180, 225, 270, 315]}
            rotateAnchorOffset={20}
            enabledAnchors={['top-left','top-center','top-right','middle-left','middle-right','bottom-left','bottom-center','bottom-right']}
            boundBoxFunc={(oldBox, newBox) => (newBox.width < 40 || newBox.height < 40 ? oldBox : newBox)}
          />
        </Layer>

        {/* Audience */}
        <Layer listening={false}>
          <Rect x={0} y={usableH} width={STAGE_W} height={AUDIENCE_H} fill="#1a1a2e" />
          {Array.from({ length: 20 }).map((_, i) => {
            const spacing = STAGE_W / 20;
            const cx = i * spacing + spacing / 2;
            return (
              <React.Fragment key={i}>
                <Rect x={cx - 10} y={usableH + 22} width={20} height={30} fill="#2d2d50" cornerRadius={[3, 3, 0, 0]} />
                <Rect x={cx - 7}  y={usableH + 10} width={14} height={14} fill="#2d2d50" cornerRadius={7} />
              </React.Fragment>
            );
          })}
          <Text x={0} y={STAGE_H - 26} width={STAGE_W} text="AUDIENCE" align="center" fontSize={13} fontStyle="bold" fontFamily="'Inter','Roboto',sans-serif" fill="#ffd700" letterSpacing={5} />
        </Layer>
      </Stage>
    </Box>
  );
};

// ─── Main component ────────────────────────────────────────────────────────────

const StageDesigner: React.FC = () => {
  const { user } = useAuth();

  const [showGrid, setShowGrid] = useState(true);
  const [placedElements, setPlacedElements] = useState<PlacedElement[]>([]);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [isLabelMode, setIsLabelMode] = useState(false);

  // Band association
  const [bands, setBands] = useState<Band[]>([]);
  const [plotBandId, setPlotBandId] = useState<string>('');

  const historyRef = useRef<PlacedElement[][]>([[]]);
  const historyIdxRef = useRef(0);
  const [historyIdx, setHistoryIdx] = useState(0);

  const [plotName, setPlotName] = useState(randomPlotName);
  const [plotDescription, setPlotDescription] = useState('');
  const [stageId, setStageId] = useState<string | undefined>();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadedPlots, setLoadedPlots] = useState<StagePlotData[]>([]);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [loadDialogOpen, setLoadDialogOpen] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved'>('idle');

  // Channel list
  const [activeTab, setActiveTab] = useState<'stage' | 'channels'>('stage');
  const [channelList, setChannelList] = useState<ChannelEntry[]>([]);
  const [stageDiff, setStageDiff] = useState<ChannelDiff | null>(null);
  const [diffDialogOpen, setDiffDialogOpen] = useState(false);
  // Track element instanceIds at last generate, to detect stage drift
  const lastGeneratedIdsRef = useRef<string>('');

  // Label / annotation
  const [labelDialogOpen, setLabelDialogOpen] = useState(false);
  const [labelDraft, setLabelDraft] = useState('');
  const labelPosRef = useRef<{ x: number; y: number } | null>(null);
  const labelEditIdRef = useRef<string | null>(null);

  // Canvas scaling
  const [canvasScale, setCanvasScale] = useState(1);
  const contentRef = useRef<HTMLDivElement>(null);

  const stageRef = useRef<Konva.Stage>(null);
  const autosaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const suggestionTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const navigate = useNavigate();
  const { id } = useParams();

  // ── Fetch user's bands for the Save dialog ────────────────────────────────

  useEffect(() => {
    if (!user?._id) return;
    axiosInstance.get<Band[]>(`/api/users/${user._id}/bands`)
      .then(res => setBands(res.data))
      .catch(() => {}); // non-critical — band selector just stays empty
  }, [user?._id]);

  // ── Canvas scale ───────────────────────────────────────────────────────────

  useLayoutEffect(() => {
    const compute = () => {
      if (!contentRef.current) return;
      const w = contentRef.current.clientWidth - 32;
      const h = contentRef.current.clientHeight - 60; // room for hint bar
      const s = Math.min(w / STAGE_W, h / STAGE_H, 1);
      setCanvasScale(Math.max(0.35, s));
    };
    compute();
    const ro = new ResizeObserver(compute);
    if (contentRef.current) ro.observe(contentRef.current);
    return () => ro.disconnect();
  }, []);

  // ── Power sources ──────────────────────────────────────────────────────────

  const powerSources = placedElements
    .filter(el => IS_POWER_SOURCE.has(el.id))
    .map(el => ({ x: el.x + el.width / 2, y: el.y + el.height / 2 }));

  const unpoweredCount = placedElements.filter(
    el => NEEDS_POWER.has(el.id) && !isNearPower(el, powerSources),
  ).length;

  // ── Suggestion auto-dismiss ────────────────────────────────────────────────

  useEffect(() => {
    if (!suggestions.length) return;
    if (suggestionTimerRef.current) clearTimeout(suggestionTimerRef.current);
    suggestionTimerRef.current = setTimeout(() => setSuggestions([]), 8000);
    return () => { if (suggestionTimerRef.current) clearTimeout(suggestionTimerRef.current); };
  }, [suggestions]);

  // ── Autosave ───────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!stageId) return;
    if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
    autosaveTimerRef.current = setTimeout(async () => {
      setSaveStatus('saving');
      try {
        const elementsToSave = placedElements.map(({ icon: _icon, ...rest }) => rest);
        await axiosInstance.put(`/api/stageplots/${stageId}`, {
          name: plotName, description: plotDescription,
          bandId: plotBandId || null,
          elements: elementsToSave,
          channelList,
        });
        setSaveStatus('saved');
        setTimeout(() => setSaveStatus('idle'), 2500);
      } catch {
        setSaveStatus('idle');
      }
    }, 3000);
    return () => { if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current); };
  }, [placedElements, stageId, plotName, plotDescription]);

  // ── History ────────────────────────────────────────────────────────────────

  const pushToHistory = useCallback((elements: PlacedElement[]) => {
    historyRef.current = historyRef.current.slice(0, historyIdxRef.current + 1);
    historyRef.current.push(elements);
    historyIdxRef.current = historyRef.current.length - 1;
    setHistoryIdx(historyIdxRef.current);
  }, []);

  const undo = useCallback(() => {
    if (historyIdxRef.current <= 0) return;
    historyIdxRef.current--;
    setHistoryIdx(historyIdxRef.current);
    setPlacedElements(historyRef.current[historyIdxRef.current]);
    setSelectedIds([]);
  }, []);

  const redo = useCallback(() => {
    if (historyIdxRef.current >= historyRef.current.length - 1) return;
    historyIdxRef.current++;
    setHistoryIdx(historyIdxRef.current);
    setPlacedElements(historyRef.current[historyIdxRef.current]);
  }, []);

  // ── Selection ─────────────────────────────────────────────────────────────

  const handleSelect = useCallback((id: string | null, shift: boolean) => {
    if (!id) { setSelectedIds([]); return; }
    if (shift) {
      setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    } else {
      setSelectedIds([id]);
    }
  }, []);

  // ── Element operations ────────────────────────────────────────────────────

  const addElement = useCallback((element: StageElement, x: number, y: number) => {
    const { width, height } = getDefaultSize(element.id);
    const newEl: PlacedElement = {
      ...element,
      x: Math.max(0, Math.round(x / GRID_SIZE) * GRID_SIZE),
      y: Math.max(0, Math.round(y / GRID_SIZE) * GRID_SIZE),
      rotation: 0,
      instanceId: `${element.id}-${Date.now()}`,
      width, height,
    };
    setPlacedElements(prev => {
      const next = [...prev, newEl];
      pushToHistory(next);
      const placedIds = new Set(next.map(e => e.id));
      const raw = ELEMENT_SUGGESTIONS[element.id] ?? [];
      const filtered = raw.filter(s => !placedIds.has(s.id));
      if (filtered.length) setSuggestions(filtered);
      return next;
    });
    setSelectedIds([newEl.instanceId]);
  }, [pushToHistory]);

  const handleElementAdd = useCallback((element: StageElement, smart: boolean) => {
    const { width, height } = getDefaultSize(element.id);
    const smartPos = smart ? SMART_POSITIONS[element.id]?.(width, height) : undefined;
    const pos = smartPos ?? { x: Math.max(0, (STAGE_W - width) / 2), y: Math.max(0, (STAGE_H - AUDIENCE_H - height) / 2) };
    addElement(element, pos.x, pos.y);
  }, [addElement]);

  const updateElement = useCallback((instanceId: string, updates: Partial<PlacedElement>) => {
    setPlacedElements(prev => {
      const next = prev.map(el => el.instanceId === instanceId ? { ...el, ...updates } : el);
      pushToHistory(next);
      return next;
    });
  }, [pushToHistory]);

  const handleGroupMove = useCallback((updates: Record<string, { x: number; y: number }>) => {
    setPlacedElements(prev => {
      const next = prev.map(el => updates[el.instanceId] ? { ...el, ...updates[el.instanceId] } : el);
      pushToHistory(next);
      return next;
    });
  }, [pushToHistory]);

  const deleteSelected = useCallback(() => {
    if (!selectedIds.length) return;
    setPlacedElements(prev => {
      const next = prev.filter(el => !selectedIds.includes(el.instanceId));
      pushToHistory(next);
      return next;
    });
    setSelectedIds([]);
  }, [selectedIds, pushToHistory]);

  const handleDrop = useCallback((data: { id: string; name: string; type: string }, x: number, y: number) => {
    const def = findElementDef(data.id);
    addElement({ id: data.id, name: data.name, type: data.type as 'microphone' | 'instrument' | 'equipment', icon: def?.icon ?? <MusicNoteIcon /> }, x, y);
  }, [addElement]);

  // ── Label / annotation ────────────────────────────────────────────────────

  const handleLabelPlace = useCallback((pos: { x: number; y: number }) => {
    labelPosRef.current = pos;
    labelEditIdRef.current = null;
    setLabelDraft('');
    setLabelDialogOpen(true);
  }, []);

  const handleEditAnnotation = useCallback((instanceId: string, currentText: string) => {
    labelEditIdRef.current = instanceId;
    labelPosRef.current = null;
    setLabelDraft(currentText);
    setLabelDialogOpen(true);
  }, []);

  const handleLabelConfirm = useCallback(() => {
    const text = labelDraft.trim();
    if (!text) { setLabelDialogOpen(false); return; }
    if (labelEditIdRef.current) {
      updateElement(labelEditIdRef.current, { name: text });
    } else if (labelPosRef.current) {
      const def: StageElement = { id: 'annotation', name: text, icon: <LabelIcon />, type: 'equipment' };
      addElement(def, labelPosRef.current.x, labelPosRef.current.y);
    }
    setLabelDialogOpen(false);
    setIsLabelMode(false);
  }, [labelDraft, addElement, updateElement]);

  // ── Keyboard shortcuts ────────────────────────────────────────────────────

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      const tag = (document.activeElement as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;
      if (e.key === 'Escape') { setIsLabelMode(false); return; }
      if (e.key === 'Delete' || e.key === 'Backspace') deleteSelected();
      if ((e.metaKey || e.ctrlKey) && e.key === 'z' && !e.shiftKey) { e.preventDefault(); undo(); }
      if ((e.metaKey || e.ctrlKey) && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) { e.preventDefault(); redo(); }
      if ((e.metaKey || e.ctrlKey) && e.key === 'a') { e.preventDefault(); setSelectedIds(placedElements.map(el => el.instanceId)); }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [deleteSelected, undo, redo, placedElements]);

  // ── Load existing plot ────────────────────────────────────────────────────

  useEffect(() => {
    if (!id) return;
    setIsLoading(true);
    axiosInstance.get(`/api/stageplots/${id}`)
      .then(({ data }) => {
        setStageId(id);
        setPlotName(data.name);
        setPlotDescription(data.description || '');
        // bandId may come back as a populated object or a raw ID string
        const rawBand = data.bandId;
        setPlotBandId(rawBand ? (typeof rawBand === 'object' ? rawBand._id : rawBand) : '');
        const elements: PlacedElement[] = data.elements.map((el: any) => {
          if (el.id === 'acoustic-piano') el.id = 'grand-piano';
          const def = findElementDef(el.id);
          return {
            id: el.id,
            name: el.name ?? def?.name ?? el.id,
            type: (el.type ?? def?.type ?? 'equipment') as 'microphone' | 'instrument' | 'equipment',
            icon: def?.icon ?? <MusicNoteIcon />,
            x: el.x ?? 0, y: el.y ?? 0,
            rotation: el.rotation ?? 0,
            instanceId: el.instanceId ?? `${el.id}-${Date.now()}`,
            width: el.width ?? getDefaultSize(el.id).width,
            height: el.height ?? getDefaultSize(el.id).height,
          };
        });
        setPlacedElements(elements);
        historyRef.current = [elements];
        historyIdxRef.current = 0;
        setHistoryIdx(0);
        // Restore channel list
        if (Array.isArray(data.channelList) && data.channelList.length > 0) {
          setChannelList(data.channelList);
          lastGeneratedIdsRef.current = elements.map((e: PlacedElement) => e.instanceId).sort().join(',');
        }
      })
      .catch(() => setError('Failed to load stage plot'))
      .finally(() => setIsLoading(false));
  }, [id]);

  // ── Save ──────────────────────────────────────────────────────────────────

  const handleSaveConfirm = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const elementsToSave = placedElements.map(({ icon: _icon, ...rest }) => rest);
      const plotData = {
        name: plotName,
        description: plotDescription,
        bandId: plotBandId || null,
        elements: elementsToSave,
        channelList,
      };
      if (stageId) {
        await axiosInstance.put(`/api/stageplots/${stageId}`, plotData);
      } else {
        const { data } = await axiosInstance.post('/api/stageplots', plotData);
        navigate(`/artist/stage-plots/${data._id}/edit`);
        setStageId(data._id);
      }
      setSaveDialogOpen(false);
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2500);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLoadClick = async () => {
    setIsLoading(true);
    try {
      const { data } = await axiosInstance.get('/api/stageplots');
      setLoadedPlots(data);
      setLoadDialogOpen(true);
    } catch { setError('Failed to load stage plots'); }
    finally { setIsLoading(false); }
  };

  const handleDeletePlot = async (plotId: string) => {
    try {
      await axiosInstance.delete(`/api/stageplots/${plotId}`);
      setLoadedPlots(prev => prev.filter(p => p._id !== plotId));
    } catch { setError('Failed to delete'); }
  };

  const canUndo = historyIdx > 0;
  const canRedo = historyIdx < historyRef.current.length - 1;
  const isUnsaved = !stageId && placedElements.length > 0;

  // Channel list helpers
  const currentElementIds = placedElements.map(e => e.instanceId).sort().join(',');
  const hasStageDiff = channelList.length > 0 && currentElementIds !== lastGeneratedIdsRef.current;

  const handleRegenerate = () => {
    const fresh = generateChannelList(placedElements);
    if (channelList.length === 0) {
      // First time — just set it
      setChannelList(fresh);
      lastGeneratedIdsRef.current = currentElementIds;
      return;
    }
    const diff = diffChannelList(channelList, fresh);
    if (diff.added.length === 0 && diff.removed.length === 0) {
      // No structural change — still merge to pick up any new ordering
      setChannelList(mergeChannelList(channelList, fresh));
      lastGeneratedIdsRef.current = currentElementIds;
      return;
    }
    setStageDiff(diff);
    setDiffDialogOpen(true);
  };

  const confirmRegenerate = () => {
    const fresh = generateChannelList(placedElements);
    setChannelList(mergeChannelList(channelList, fresh));
    lastGeneratedIdsRef.current = currentElementIds;
    setDiffDialogOpen(false);
    setStageDiff(null);
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <Box sx={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', zIndex: 1300, bgcolor: 'background.default' }}>
      <StageElementsDrawer onElementAdd={handleElementAdd} />

      <Box component="main" sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <AppBar position="static" sx={{ zIndex: theme => theme.zIndex.drawer + 1 }}>
          <Toolbar variant="dense">
            <Button
              color="inherit"
              size="small"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate(-1)}
              sx={{ ml: `${DRAWER_WIDTH}px`, mr: 1, textTransform: 'none', fontWeight: 500 }}
            >
              Exit
            </Button>
            <Typography variant="h6" noWrap sx={{ flexGrow: 1, fontSize: '1rem' }}>{plotName}</Typography>

            {/* Save status */}
            {saveStatus === 'saving' && (
              <Typography variant="caption" sx={{ mr: 1, opacity: 0.8 }}>Saving…</Typography>
            )}
            {saveStatus === 'saved' && (
              <Tooltip title="All changes saved">
                <SavedIcon sx={{ mr: 1, fontSize: 18, opacity: 0.85 }} />
              </Tooltip>
            )}
            {isUnsaved && saveStatus === 'idle' && (
              <Tooltip title="You have unsaved changes — click Save to keep this plot">
                <Typography variant="caption" sx={{ mr: 1, color: '#ffc107', fontWeight: 600 }}>Unsaved</Typography>
              </Tooltip>
            )}

            <Tooltip title="Undo (⌘Z)"><span><IconButton color="inherit" onClick={undo} disabled={!canUndo} size="small"><UndoIcon /></IconButton></span></Tooltip>
            <Tooltip title="Redo (⌘⇧Z)"><span><IconButton color="inherit" onClick={redo} disabled={!canRedo} size="small"><RedoIcon /></IconButton></span></Tooltip>

            {selectedIds.length > 0 && (
              <Tooltip title={`Delete ${selectedIds.length > 1 ? selectedIds.length + ' items' : 'selected'} (Del)`}>
                <IconButton color="inherit" onClick={deleteSelected} size="small"><DeleteIcon /></IconButton>
              </Tooltip>
            )}

            <Tooltip title={isLabelMode ? 'Cancel label (Esc)' : 'Add text label to stage'}>
              <IconButton
                color={isLabelMode ? 'default' : 'inherit'}
                onClick={() => setIsLabelMode(v => !v)}
                size="small"
                sx={{ backgroundColor: isLabelMode ? '#f9a825' : 'transparent', '&:hover': { backgroundColor: isLabelMode ? '#f0a000' : undefined } }}
              >
                <LabelIcon />
              </IconButton>
            </Tooltip>

            <Tooltip title={showGrid ? 'Hide grid' : 'Show grid'}>
              <IconButton color="inherit" onClick={() => setShowGrid(v => !v)} size="small"><GridIcon /></IconButton>
            </Tooltip>
            <Tooltip title="Save"><IconButton color="inherit" onClick={() => setSaveDialogOpen(true)} size="small"><SaveIcon /></IconButton></Tooltip>
            <Tooltip title="Load"><IconButton color="inherit" onClick={handleLoadClick} disabled={isLoading} size="small"><LoadIcon /></IconButton></Tooltip>
          </Toolbar>
        </AppBar>

        {error && <Box sx={{ px: 2, py: 0.5, backgroundColor: 'error.light' }}><Typography variant="body2" color="error.contrastText">{error}</Typography></Box>}

        {/* Label mode banner */}
        {isLabelMode && (
          <Box sx={{ px: 2, py: 0.75, backgroundColor: '#fff8e1', borderBottom: '1px solid #ffe082', display: 'flex', alignItems: 'center', gap: 1 }}>
            <LabelIcon sx={{ fontSize: 16, color: '#f9a825' }} />
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#e65100' }}>
              Label mode — click anywhere on stage to place a text label. Press Esc to cancel.
            </Typography>
          </Box>
        )}

        {/* Power warning banner */}
        {unpoweredCount > 0 && (
          <Box sx={{ px: 2, py: 0.75, backgroundColor: '#fff3e0', borderBottom: '1px solid #ffe0b2', display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" sx={{ color: '#e65100' }}>
              ⚡ <strong>{unpoweredCount} item{unpoweredCount > 1 ? 's' : ''}</strong> {unpoweredCount > 1 ? 'need' : 'needs'} mains power with no Power Drop nearby — add a <strong>Power Drop</strong> or <strong>Power Strip</strong> from the Equipment › Power section and place it close to those items.
            </Typography>
          </Box>
        )}

        {/* Suggestion strip */}
        {suggestions.length > 0 && (
          <Box sx={{ px: 2, py: 0.75, backgroundColor: '#e8f4fd', display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', borderBottom: '1px solid #bee3f8' }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#1565c0', mr: 0.5 }}>💡 You might also need:</Typography>
            {suggestions.map(s => (
              <Tooltip key={s.id} title={s.reason} placement="bottom">
                <Chip label={s.label} size="small" onClick={() => {
                  const def = findElementDef(s.id);
                  if (!def) return;
                  handleElementAdd(def, Boolean(SMART_POSITIONS[s.id]));
                  setSuggestions(prev => prev.filter(x => x.id !== s.id));
                }} sx={{ cursor: 'pointer', fontWeight: 500 }} />
              </Tooltip>
            ))}
            <IconButton size="small" onClick={() => setSuggestions([])} sx={{ ml: 'auto' }}><CloseIcon fontSize="small" /></IconButton>
          </Box>
        )}

        {selectedIds.length > 1 && (
          <Box sx={{ px: 2, py: 0.5, backgroundColor: '#e3f2fd', borderBottom: '1px solid #bbdefb' }}>
            <Typography variant="caption" color="primary">
              {selectedIds.length} items selected — drag any to move all · Del to delete all · click empty area to deselect
            </Typography>
          </Box>
        )}

        {/* Stage / Channel List tab bar */}
        <Box sx={{ display: 'flex', borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
          {(['stage', 'channels'] as const).map(tab => (
            <Button
              key={tab}
              onClick={() => setActiveTab(tab)}
              size="small"
              sx={{
                px: 3, py: 0.75,
                borderRadius: 0,
                borderBottom: activeTab === tab ? '2px solid' : '2px solid transparent',
                borderBottomColor: activeTab === tab ? 'primary.main' : 'transparent',
                fontWeight: activeTab === tab ? 700 : 400,
                color: activeTab === tab ? 'primary.main' : 'text.secondary',
                textTransform: 'none',
              }}
            >
              {tab === 'stage' ? 'Stage Plot' : `Channel List${channelList.length > 0 ? ` (${channelList.length})` : ''}`}
              {tab === 'channels' && hasStageDiff && (
                <Box component="span" sx={{ ml: 0.75, width: 8, height: 8, borderRadius: '50%', bgcolor: 'warning.main', display: 'inline-block' }} />
              )}
            </Button>
          ))}
        </Box>

        {/* Canvas area */}
        <Box
          ref={contentRef}
          sx={{ flexGrow: 1, overflow: 'hidden', p: 1, display: activeTab === 'stage' ? 'flex' : 'none', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}
        >
          {isLoading && !placedElements.length ? (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}><CircularProgress /></Box>
          ) : (
            <>
              <KonvaCanvas
                showGrid={showGrid} gridSize={GRID_SIZE} scale={canvasScale}
                isLabelMode={isLabelMode}
                placedElements={placedElements}
                selectedIds={selectedIds}
                powerSources={powerSources}
                onSelect={handleSelect}
                onSelectMultiple={setSelectedIds}
                onChange={updateElement}
                onGroupMove={handleGroupMove}
                onDrop={handleDrop}
                onLabelPlace={handleLabelPlace}
                onEditAnnotation={handleEditAnnotation}
                stageRef={stageRef}
              />
              <Box sx={{ mt: 0.5, display: 'flex', gap: 3, opacity: 0.55 }}>
                <Typography variant="caption">
                  Drag to place · Shift+click or drag empty area to multi-select · ⌘A select all · Del to delete
                </Typography>
                <Typography variant="caption">
                  Double-click items with blue stripe for smart placement · drag corners to resize · rotate handle ↻
                </Typography>
              </Box>
            </>
          )}
        </Box>

        {/* Channel list panel */}
        {activeTab === 'channels' && (
          <Box sx={{ flexGrow: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <ChannelListEditor
              channels={channelList}
              onChange={setChannelList}
              onRegenerate={handleRegenerate}
              hasStageDiff={hasStageDiff}
            />
          </Box>
        )}
      </Box>

      {/* Regenerate diff confirm dialog */}
      <Dialog open={diffDialogOpen} onClose={() => setDiffDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Update Channel List?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            The stage has changed since you last generated the channel list. Here's what will change:
          </Typography>
          {stageDiff?.added.length ? (
            <Box sx={{ mb: 1 }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: 'success.dark' }}>Added</Typography>
              {stageDiff.added.map(n => (
                <Typography key={n} variant="body2" sx={{ pl: 1.5 }}>+ {n}</Typography>
              ))}
            </Box>
          ) : null}
          {stageDiff?.removed.length ? (
            <Box sx={{ mb: 1 }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: 'warning.dark' }}>Removed (flagged ⚠, not deleted)</Typography>
              {stageDiff.removed.map(n => (
                <Typography key={n} variant="body2" sx={{ pl: 1.5 }}>− {n}</Typography>
              ))}
            </Box>
          ) : null}
          <Typography variant="caption" color="text.secondary">
            Your edits to existing channels are preserved.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDiffDialogOpen(false)}>Cancel</Button>
          <Button onClick={confirmRegenerate} variant="contained">Update Channel List</Button>
        </DialogActions>
      </Dialog>

      {/* Save dialog */}
      <Dialog open={saveDialogOpen} onClose={() => setSaveDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{stageId ? 'Update Stage Plot' : 'Save Stage Plot'}</DialogTitle>
        <DialogContent>
          <TextField autoFocus margin="dense" label="Plot Name" fullWidth value={plotName} onChange={e => setPlotName(e.target.value)} sx={{ mb: 2 }} />
          <TextField margin="dense" label="Description (optional)" fullWidth multiline rows={2} value={plotDescription} onChange={e => setPlotDescription(e.target.value)} sx={{ mb: 2 }} />
          <FormControl fullWidth size="small">
            <InputLabel>Band (optional)</InputLabel>
            <Select
              value={plotBandId}
              label="Band (optional)"
              onChange={e => setPlotBandId(e.target.value)}
            >
              <MenuItem value=""><em>No band — unlinked</em></MenuItem>
              {bands.map(b => (
                <MenuItem key={b._id} value={b._id}>{b.artistName}</MenuItem>
              ))}
            </Select>
          </FormControl>
          {bands.length === 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              You have no bands yet — you can link this plot to a band later from the Stage Plots page.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveConfirm} disabled={!plotName || isLoading} variant="contained">
            {isLoading ? 'Saving…' : stageId ? 'Update' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Load dialog */}
      <Dialog open={loadDialogOpen} onClose={() => setLoadDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Load Stage Plot</DialogTitle>
        <DialogContent>
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress /></Box>
          ) : loadedPlots.length === 0 ? (
            <Typography sx={{ p: 2, textAlign: 'center' }}>No saved stage plots found</Typography>
          ) : (
            <List>
              {loadedPlots.map(plot => (
                <ListItem key={plot._id} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, mb: 1 }}
                  secondaryAction={
                    <Box>
                      <IconButton onClick={() => handleDeletePlot(plot._id ?? '')} sx={{ mr: 1 }}><DeleteIcon /></IconButton>
                      <Button variant="contained" onClick={() => { setLoadDialogOpen(false); navigate(`/artist/stage-plots/${plot._id}/edit`); }}>Load</Button>
                    </Box>
                  }
                >
                  <ListItemText
                    primary={plot.name}
                    secondary={
                      <>
                        {plot.description && <Typography variant="body2" color="text.secondary">{plot.description}</Typography>}
                        <Typography variant="caption" color="text.secondary">
                          {plot.elements?.length ?? 0} items · {new Date(plot.createdAt ?? Date.now()).toLocaleDateString()}
                        </Typography>
                      </>
                    }
                  />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions><Button onClick={() => setLoadDialogOpen(false)}>Close</Button></DialogActions>
      </Dialog>

      {/* Label / annotation dialog */}
      <Dialog open={labelDialogOpen} onClose={() => setLabelDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{labelEditIdRef.current ? 'Edit Label' : 'Add Label'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus fullWidth margin="dense"
            label="Label text"
            placeholder="e.g. Kenneth – Stage Left"
            value={labelDraft}
            onChange={e => setLabelDraft(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') handleLabelConfirm(); }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLabelDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleLabelConfirm} disabled={!labelDraft.trim()} variant="contained">
            {labelEditIdRef.current ? 'Update' : 'Place'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StageDesigner;
